import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { DARK_THEME, LIGHT_THEME, DARK_STATUS_STYLES, LIGHT_STATUS_STYLES } from "../theme";
import { DARK_TOKENS, LIGHT_TOKENS, SPACING, TYPOGRAPHY, RADIUS } from "../theme/tokens";
import { getLocal, setLocal } from "../utils/storageUtils";

const IS_WINDOWS = /Windows/i.test(navigator.userAgent);

export function useTheme() {
  const [isDark, setIsDark] = useState(() => getLocal("sa_lax_theme", "dark") !== "light");
  const [fontScale, setFontScaleState] = useState(() => getLocal("sa_lax_font_scale", 1));
  const [headerHeight, setHeaderHeight] = useState(0);
  const roRef = useRef(null);

  // Callback ref: sets up ResizeObserver whenever the header element mounts.
  // Using a plain useRef + [] effect fails when the header mounts after the
  // initial render (e.g. first login from a logged-out state).
  const headerRef = useCallback(el => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!el) return;
    const ro = new ResizeObserver(entries => setHeaderHeight(entries[0].contentRect.height));
    ro.observe(el);
    roRef.current = ro;
  }, []);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    setLocal("sa_lax_theme", next ? "dark" : "light");
  };

  const setFontScale = (scale) => {
    setFontScaleState(scale);
    setLocal("sa_lax_font_scale", scale);
  };

  const SA = isDark ? DARK_THEME : LIGHT_THEME;
  const STATUS_STYLES = isDark ? DARK_STATUS_STYLES : LIGHT_STATUS_STYLES;
  const tokens = isDark ? DARK_TOKENS : LIGHT_TOKENS;

  const GLASS = useMemo(() => ({
    // Entry cards — physics: white tint (a=0), light from top-left (-45°)
    // Windows: backdrop-filter disabled — Chrome on Windows repaints all blur layers on any
    // hover/paint event, causing visible flicker. Use an opaque solid background instead.
    card: IS_WINDOWS ? {
      background: isDark
        ? "linear-gradient(135deg, rgba(22,32,56,0.97) 0%, rgba(16,24,44,0.99) 100%)"
        : "linear-gradient(135deg, rgba(240,244,255,0.98) 0%, rgba(228,236,255,0.96) 100%)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.72)"}`,
      boxShadow: isDark
        ? "inset 0 1.5px 0 rgba(255,255,255,0.55), inset -1px -1px 0 rgba(255,255,255,0.08), 0 10px 28px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.14)"
        : "inset 0 1.5px 0 rgba(255,255,255,0.95), inset -1px -1px 0 rgba(255,255,255,0.40), 0 6px 20px rgba(0,0,0,0.08)",
      borderRadius: 20,
    } : {
      backdropFilter: "blur(20px) saturate(190%)",
      WebkitBackdropFilter: "blur(20px) saturate(190%)",
      // White tint at low alpha — glass color comes from blurred ambient blobs behind it
      background: isDark
        ? "linear-gradient(135deg, rgba(255,255,255,0.13) 0%, rgba(255,255,255,0.05) 100%)"
        : "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.52) 100%)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.72)"}`,
      // Primary specular (top-left, -45°) + secondary bounce (bottom-right, 80% of primary)
      boxShadow: isDark
        ? "inset 0 1.5px 0 rgba(255,255,255,0.55), inset -1px -1px 0 rgba(255,255,255,0.08), 0 10px 28px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.14)"
        : "inset 0 1.5px 0 rgba(255,255,255,0.95), inset -1px -1px 0 rgba(255,255,255,0.40), 0 6px 20px rgba(0,0,0,0.08)",
      borderRadius: 20,
    },
    // Navigation panels — heavy frosted glass; cards sliding under get blurred + colour-boosted
    panel: {
      backdropFilter: "blur(20px) saturate(260%) brightness(106%)",
      WebkitBackdropFilter: "blur(20px) saturate(260%) brightness(106%)",
      background: isDark ? "rgba(6,10,20,0.74)" : "rgba(255,255,255,0.62)",
      borderBottom: `2px solid ${isDark ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.98)"}`,
      boxShadow: isDark
        ? "inset 0 -1px 0 rgba(255,255,255,0.10), 0 8px 48px rgba(0,0,0,0.50), 0 1px 0 rgba(0,0,0,0.22)"
        : "inset 0 -1px 0 rgba(180,200,255,0.55), 0 8px 36px rgba(80,100,180,0.28), 0 1px 0 rgba(80,100,180,0.14)",
    },
    // Modal sheets — navy tint in dark mode keeps ambient blobs from bleeding through
    modal: {
      backdropFilter: "blur(48px) saturate(180%)",
      WebkitBackdropFilter: "blur(48px) saturate(180%)",
      background: isDark
        ? "linear-gradient(135deg, rgba(8,14,28,0.82) 0%, rgba(6,10,22,0.88) 100%)"
        : "linear-gradient(135deg, rgba(248,250,255,0.97) 0%, rgba(240,246,255,0.95) 100%)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.09)"}`,
      // Primary specular top-left + secondary bounce bottom-right (80% intensity)
      // Shadow: y=+10px, factor=15% → rgba(0,0,0,0.15), spread=25px
      boxShadow: isDark
        ? "inset 0 1.5px 0 rgba(255,255,255,0.65), inset -1px -1px 0 rgba(255,255,255,0.12), 0 10px 25px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.14)"
        : "inset 0 1.5px 0 rgba(255,255,255,1), inset -1px -1px 0 rgba(255,255,255,0.55), 0 10px 25px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)",
      borderRadius: 28,
    },
    input: {
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(80,100,160,0.22)"}`,
      borderRadius: 10,
    },
    button: {
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      background: isDark ? "rgba(91,142,255,0.75)" : "rgba(37,99,235,0.80)",
      border: "1px solid rgba(255,255,255,0.22)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.28), 0 4px 16px rgba(91,142,255,0.35)",
    },
  }), [isDark]);

  const TYPOGRAPHY_SCALED = useMemo(() => fontScale === 1 ? TYPOGRAPHY : Object.fromEntries(
    Object.entries(TYPOGRAPHY).map(([k, v]) => [k, Math.round(v * fontScale)])
  ), [fontScale]);

  return { isDark, toggleTheme, SA, STATUS_STYLES, GLASS, tokens, SPACING, TYPOGRAPHY: TYPOGRAPHY_SCALED, RADIUS, headerRef, headerHeight, fontScale, setFontScale };
}
